/* ============================================================================
   cloudtec Plattform-Konfigurator — i18n
   ----------------------------------------------------------------------------
   Strings live in site/i18n/<locale>.json. Two locales: 'de' (default), 'en'.
   The current locale is persisted in localStorage and toggled via the header.
   ============================================================================ */

const I18N_LOCALES = ['de', 'en'];
const I18N_DEFAULT = 'de';
const I18N_LS_KEY  = 'cloudtec.konfig.locale.v1';

let _locale  = I18N_DEFAULT;
let _strings = {};
let _ready   = false;
const _missingKeysSeen = new Set();

function _detectInitialLocale() {
  // Priority: explicit user choice (LS) → server-injected host locale → browser → default
  try {
    const stored = localStorage.getItem(I18N_LS_KEY);
    if (stored && I18N_LOCALES.includes(stored)) return stored;
  } catch (e) {}
  const injected = (typeof window !== 'undefined' && window.__APP_LOCALE__ || '').toLowerCase();
  if (I18N_LOCALES.includes(injected)) return injected;
  const navLang = (typeof navigator !== 'undefined' && navigator.language || '').slice(0, 2).toLowerCase();
  if (I18N_LOCALES.includes(navLang)) return navLang;
  return I18N_DEFAULT;
}

/**
 * Load and apply a locale. Pass { persist: true } only for an explicit user
 * choice (the header switcher) — that writes localStorage so it survives
 * reloads and wins over the server-injected host locale. Auto/boot loads must
 * NOT persist, or the domain default would stick forever.
 */
async function loadLocale(locale, { persist = false } = {}) {
  if (!I18N_LOCALES.includes(locale)) locale = I18N_DEFAULT;
  try {
    const res = await fetch(`i18n/${locale}.json`, { cache: 'no-cache' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    _strings = await res.json();
    _locale = locale;
    _ready = true;
    if (persist) {
      try { localStorage.setItem(I18N_LS_KEY, locale); } catch (e) {}
    }
    if (typeof document !== 'undefined') {
      document.documentElement.lang = locale;
      document.dispatchEvent(new CustomEvent('locale-changed', { detail: { locale } }));
    }
  } catch (e) {
    console.error(`[i18n] failed to load locale "${locale}":`, e);
    if (locale !== I18N_DEFAULT) await loadLocale(I18N_DEFAULT);
  }
}

function _byPath(obj, key) {
  return key.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

/**
 * Translate a key. Supports {placeholder} interpolation.
 *   t('pre.title')
 *   t('errors.ki_failed', { message: '…' })
 *   t('counter', { count: 23, max: 1000 })
 */
function t(key, params) {
  if (!_ready) return key;
  let s = _byPath(_strings, key);
  if (typeof s !== 'string') {
    if (!_missingKeysSeen.has(key)) {
      _missingKeysSeen.add(key);
      console.warn(`[i18n] missing key in "${_locale}": ${key}`);
    }
    return key;
  }
  if (params) {
    for (const p in params) {
      s = s.split(`{${p}}`).join(String(params[p]));
    }
  }
  return s;
}

/** Returns current locale ('de' | 'en'). */
function currentLocale() { return _locale; }

/** Returns true once a locale has been loaded. */
function i18nReady() { return _ready; }

/** Returns raw strings object — for debugging only. */
function _i18nStrings() { return _strings; }

/**
 * Resolve a label for an item from a fixed list (modules, platform types, …).
 * Tries `${group}.${item.id}` from i18n; falls back to item.label or item.id.
 *   tLabel('platform_types', { id: 'kundenportal', label: 'Kundenportal' })
 */
function tLabel(group, item) {
  if (!item) return '';
  const key = `${group}.${item.id}`;
  const s = _byPath(_strings, key);
  if (typeof s === 'string') return s;
  return item.label || item.id;
}

/** Variant for {title, sub} pairs used by PROJECT_KINDS / COMPANY_KINDS. */
function tLabelPair(group, item) {
  if (!item) return { title: '', sub: '' };
  const t1 = _byPath(_strings, `${group}.${item.id}.title`);
  const t2 = _byPath(_strings, `${group}.${item.id}.sub`);
  return {
    title: typeof t1 === 'string' ? t1 : (item.title || item.id),
    sub:   typeof t2 === 'string' ? t2 : (item.sub   || ''),
  };
}

// Auto-load on script execution. App starts after the locale is loaded.
const _initialLocale = _detectInitialLocale();
const _i18nBoot = loadLocale(_initialLocale);

export {
  I18N_LOCALES, I18N_DEFAULT,
  loadLocale, t, tLabel, tLabelPair, currentLocale, i18nReady, _i18nStrings, _i18nBoot,
};
